import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import type { SellerPayoutSchedule } from '@util/firestore/orders';
import { formatTimestamp } from '@util/index';
import type { StatusBadgeProps, StatusType } from './StatusBadge';

const getStatusText = (
  status: StatusType,
  variant: 'buyer' | 'seller',
  is_partial?: boolean
): string => {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'in-transit':
      return 'In Transit';
    case 'dropped-off':
      return 'Dropped Off';
    case 'awaiting-dropoff':
      return 'Awaiting Drop-off';
    case 'awaiting-shipment':
      return variant === 'seller' ? 'Awaiting Drop-off' : 'Order Placed';
    case 'order-placed':
      return 'Order Placed';
    case 'delivered':
      return 'Delivered';
    case 'on-hold':
      return 'On Hold';
    case 'delivery-error':
      return 'Delivery Error';
    case 'cancelled':
      return 'Cancelled';
    case 'refunded':
      return is_partial ? 'Partially Refunded' : 'Refunded';
    default:
      return status;
  }
};

export const StatusBadgeNew = ({
  status,
  is_partial,
  date,
  variant,
  compact = false,
  hasLeftReview,
  tracking_number,
  payoutSchedule,
  payoutScheduleLoading = false,
  showPayoutSchedule = false,
}: StatusBadgeProps & {
  date?: string;
  variant: 'buyer' | 'seller';
  compact?: boolean;
  hasLeftReview?: boolean;
  tracking_number?: string;
  payoutSchedule?: SellerPayoutSchedule;
  payoutScheduleLoading?: boolean;
  /**
   * Shows the seller's payout timing on a view that is not the seller's own.
   * Support answers "when does this seller get paid" from the buyer-side
   * order page, and the callable already authorises them - this is only
   * about what the buyer variant renders.
   */
  showPayoutSchedule?: boolean;
}) => {
  if (compact) {
    return (
      <span className="text-[1.6rem] font-medium text-zinc-700">
        {getStatusText(status, variant, is_partial)}
      </span>
    );
  }
  if (status === 'completed') {
    return (
      <div className="flex flex-col gap-[0.8rem]">
        {hasLeftReview || variant == 'seller' ? (
          <>
            <span className="text-[1.75rem] font-semibold text-[#1E7A1E] sm:text-[2.25rem]">
              Completed
            </span>
            {variant === 'buyer' && (
              <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
                Thank you for shopping on MX Locker!
              </span>
            )}
          </>
        ) : (
          <>
            <span className="text-[1.75rem] font-semibold sm:text-[2.25rem]">
              {date ? `Delivered ${date}` : 'Delivered'}
            </span>
            {variant === 'buyer' && (
              <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
                Your order arrived at its final destination.
              </span>
            )}
          </>
        )}
        {variant === 'seller' && (
          <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
            This order is now complete. Funds are available on your MX Locker
            balance.
          </span>
        )}
      </div>
    );
  } else if (status === 'in-transit') {
    return (
      <div className="flex flex-col">
        <span className="text-[1.75rem] font-semibold text-[#1E7A1E] sm:text-[2.25rem]">
          Arriving {date ? date : 'Soon'}
        </span>
        <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
          Your order is on the way! Delivery is expected soon.
        </span>
      </div>
    );
  } else if (status === 'dropped-off') {
    return (
      <div className="flex flex-col gap-[1.6rem]">
        <span className="text-[1.75rem] font-semibold sm:text-[2.25rem] ">
          Dropped Off
        </span>
        {variant === 'seller' && (
          <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
            This order has been dropped off. Tracking will update once the
            carrier scans the package, which may take up to 24 hours.
          </span>
        )}
        {variant === 'buyer' && (
          <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
            The seller has dropped off your order with the carrier. Tracking
            will update as soon as it’s scanned in, which can take up to 24
            hours.
          </span>
        )}
      </div>
    );
  } else if (status === 'awaiting-dropoff' || status === 'awaiting-shipment') {
    return (
      <div className="flex flex-col">
        {date && variant === 'seller' && (
          <span className="text-[1.75rem] font-semibold text-brand-secondary sm:text-[2.25rem]">
            Ship Before {date}
          </span>
        )}
        <span className="text-[1.75rem] font-semibold sm:text-[2.25rem]">
          {variant === 'seller' || status === 'awaiting-dropoff'
            ? 'Awaiting Drop-Off'
            : 'Order Placed'}
        </span>
        {variant === 'buyer' && status === 'awaiting-shipment' && (
          <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
            {tracking_number
              ? 'Tracking has been uploaded.'
              : 'Tracking will be uploaded shortly.'}{' '}
            Expect your item to ship within 1–3 business days.
          </span>
        )}
        {variant === 'buyer' && status === 'awaiting-dropoff' && (
          <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
            Tracking has been uploaded. We&apos;re now waiting for the carrier
            to scan in the item.
          </span>
        )}
        {variant === 'seller' && status === 'awaiting-dropoff' && (
          <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
            A pre-paid label has been provided. Print the label and drop off the
            package at the carrier listed on the label.
          </span>
        )}
        {variant === 'seller' && status === 'awaiting-shipment' && (
          <span className="mb-[1.6rem] block text-[1.2rem] font-medium sm:text-[1.4rem]">
            Provide your own tracking or print a shipping label by the due date.
          </span>
        )}
        {date && variant === 'buyer' && !tracking_number && (
          <Disclosure>
            <div className="mt-[1.2rem] flex items-center justify-between gap-[1.2rem] rounded-[1.2rem] bg-[#f0f2f2] px-[1.6rem] py-[1.2rem]">
              <span className="min-w-0 text-[1.3rem] font-semibold text-black sm:text-[1.5rem]">
                Seller has until{' '}
                <strong className="font-semibold text-brand-secondary">
                  {date}
                </strong>{' '}
                to ship
              </span>
              <DisclosureButton className="relative flex h-[2.4rem] w-[2.4rem] shrink-0 items-center justify-center rounded-full bg-brand-secondary">
                <span className="text-[1.4rem] font-semibold text-white">
                  i
                </span>
                <DisclosurePanel className="absolute right-0 top-full z-20 mt-2 w-[40rem] rounded-lg bg-white p-4 shadow-lg">
                  Sellers have 3 business days to ship. Orders not shipped
                  within 5 business days are eligible for a full refund.
                </DisclosurePanel>
              </DisclosureButton>
            </div>
          </Disclosure>
        )}
      </div>
    );
  } else if (status === 'order-placed') {
    return (
      <div className="flex flex-col gap-[1.6rem]">
        <span className="text-[1.75rem] font-semibold sm:text-[2.25rem]">
          Order Placed
        </span>
        <span className="text-[1.75rem] font-semibold sm:text-[2.25rem]">
          Tracking number will be available within 3 business days
        </span>
      </div>
    );
  } else if (status === 'delivered') {
    // delivered
    return (
      <div className="flex flex-col gap-[0.8rem]">
        <span className="text-[1.75rem] font-semibold sm:text-[2.25rem]">
          {date ? `Delivered ${date}` : 'Delivered'}
        </span>
        {variant === 'buyer' && (
          <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
            Your package has been delivered.
          </span>
        )}
        {(variant === 'seller' || showPayoutSchedule) && (
          <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
            {payoutScheduleLoading
              ? 'Loading payout schedule…'
              : payoutSchedule?.scheduled_at
              ? `${
                  payoutSchedule.source === 'task'
                    ? 'Payout scheduled for'
                    : 'Expected payout'
                } ${formatTimestamp(payoutSchedule.scheduled_at)}.`
              : 'Payout timing is being finalized.'}
          </span>
        )}
      </div>
    );
  } else if (status === 'on-hold') {
    return (
      <div className="flex items-center gap-[0.4rem]">
        <span className="text-[1.75rem] font-semibold text-brand-secondary sm:text-[2.25rem]">
          On Hold
        </span>
      </div>
    );
  } else if (status === 'delivery-error') {
    return (
      <div className="flex items-center gap-[0.4rem]">
        <span className="text-[1.75rem] font-semibold text-black sm:text-[2.25rem]">
          Delivery Error
        </span>
      </div>
    );
  } else if (status === 'cancelled') {
    return (
      <div className="flex flex-col gap-[0.8rem]">
        <span className="text-[1.75rem] font-semibold text-black sm:text-[2.25rem]">
          Cancelled
        </span>
        {variant === 'buyer' && (
          <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
            This order was cancelled. No further action is needed.
          </span>
        )}
      </div>
    );
  } else if (status === 'refunded') {
    return (
      <div className="flex flex-col gap-[0.8rem]">
        <span className="text-[1.75rem] font-semibold text-black sm:text-[2.25rem]">
          {is_partial ? 'Partially Refunded' : 'Refunded'}
        </span>
        {variant === 'buyer' && (
          <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
            Your refund has been processed. Funds will return to your payment
            method within 1–3 business days.
          </span>
        )}
        {variant === 'seller' && (
          <span className="text-[1.2rem] font-medium sm:text-[1.4rem]">
            This refund has been processed. Funds have been returned to the
            customer.
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-[1.6rem]">
      <div className="flex gap-[0.4rem]">
        <span>{status}</span>
      </div>
    </div>
  );
};
